import type {CreateExtArgs, Extension, ExtensionAPI, DmApis, IndexKey} from '@wix/document-manager-core'
import _ from 'lodash'
import {DAL_READ_INDEX, DAL_READ_VALUES, VIEWER_UPDATED_EVENT} from '../../constants/constants'
import type {Pointer} from '@wix/document-services-types'

interface Observable<T> {
    get(): T
    set(value: T): void
}

type CreateObservableFunction = <T>() => Observable<T>

interface ObservablesAPI extends ExtensionAPI {
    registerObservableNotifier(createObservable: CreateObservableFunction): void
}

interface ObservablesExtensionAPI extends ExtensionAPI {
    observables: ObservablesAPI
}

type ObservableIdentifier = string
type ObservableStores = Record<ObservableIdentifier, Observable<boolean>>

interface DALReadIndexArgs {
    namespacesKeys: string[]
    indexKey: IndexKey
}

/**
 * @returns {Extension}
 */
const createExtension = (): Extension => {
    let _createObservable: (initialValue?: boolean) => Observable<boolean>
    let observableNotifierWasRegistered = false
    const observableStores: ObservableStores = {}

    const createExtensionAPI = ({eventEmitter, dal, coreConfig}: CreateExtArgs): ObservablesExtensionAPI => {
        const dispatchChangeInObservableStore = (type: string) => {
            if (observableStores[type]) {
                const value = observableStores[type].get()
                observableStores[type].set(!value)
            }
        }

        const subscribeToObservableStore = (pointer: Pointer) => {
            const key = pointer.type.endsWith('-ref') ? pointer.type.slice(0, -4) : pointer.type
            if (!observableStores[key]) {
                observableStores[key] = _createObservable?.(true)
            }
            observableStores[key]?.get()
        }

        const registerObservableNotifier = (createObservable: CreateObservableFunction) => {
            if (!coreConfig.experimentInstance.isOpen('dm_observables')) {
                return
            }

            if (observableNotifierWasRegistered) {
                throw new Error('Observable notifier was already registered')
            }

            observableNotifierWasRegistered = true
            _createObservable = createObservable

            dal.enableShouldNotifyAboutReads()

            eventEmitter.addListener(VIEWER_UPDATED_EVENT, async (pointersUpdated: Pointer[]) => {
                _(pointersUpdated)
                    .uniqBy('type')
                    .forEach(pointer => {
                        dispatchChangeInObservableStore(pointer.type)
                    })
            })

            eventEmitter.addListener(DAL_READ_VALUES, async (pointersToObserve: Pointer[]) => {
                pointersToObserve.forEach(pointerToObserve => {
                    subscribeToObservableStore(pointerToObserve)
                })
            })

            eventEmitter.addListener(DAL_READ_INDEX, async (args: DALReadIndexArgs) => {
                const {namespacesKeys} = args
                if (!namespacesKeys) {
                    return
                }
                namespacesKeys.forEach((namespaceKey: string) => {
                    subscribeToObservableStore({type: namespaceKey} as Pointer)
                })
            })
        }
        return {
            observables: {
                registerObservableNotifier
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {observables} = extensionAPI as ObservablesExtensionAPI
        return {
            observables: {
                registerObservableNotifier(createObservable: CreateObservableFunction): void {
                    return observables.registerObservableNotifier(createObservable)
                }
            }
        }
    }

    return {
        name: 'observables',
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension, ObservablesExtensionAPI, ObservablesAPI}
